import { Component, OnInit, OnDestroy, input, signal, SimpleChanges } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { ZorroModule } from '../../../../modules/zorro.module';
import { AvatarComponent } from '../../avatar/avatar.component';
import { Subscription, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CognitoService } from '../../../../services/cognito.service';
import { Activity, Client, Content, Course, Lesson, SetupWizardState, Unit } from '@mira-avant/mtlibs';
import { ActivatedRoute } from '@angular/router';
import { ClientService } from '../../../../services/client.service';

interface Breadcrumb {
	name: string;
	route: (string | object)[];
	label: string;
	userUuid?: string;
	userPoolUuid?: string;
	displayName?: string | null;
}

export enum AdminProducts {
	LC = 'lc',
	CONTENT_GENERATION = 'content-generation',
	IAM = 'iam',
	RUBRICS = 'rubrics',
	PURCHASES = 'purchases',
	PRODUCTS = 'products',
	PRODUCT_STATUS = 'product-status',
	MEDIA_DEPOT = 'media-depot',
	CONTENT_APP = 'content-app',
	SETUP_WIZARD = 'setup-wizard',
	SECTION_ENROLLMENT = 'section-enrollment',
	STRIDE_ALGORITHM_REPORT = 'stride-algorithm-report',
}

@Component({
	selector: 'app-breadcrumbs',
	standalone: true,
	imports: [AngularModule, ZorroModule, AvatarComponent],
	templateUrl: './breadcrumbs.component.html',
	styleUrl: './breadcrumbs.component.scss',
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
	public selectedClient = input<Client | null | undefined>(undefined);
	public breadcrumbs = signal<Breadcrumb[]>([]);
	private sub: Subscription | undefined;
	private clientSub: Subscription | undefined;

	constructor(
		private route: ActivatedRoute,
		public nav: NavigationService,
		public adminService: AdminService,
		private cognitoService: CognitoService,
		private clientService: ClientService
	) {}

	ngOnInit() {
		this.updateBreadcrumbs();

		this.clientSub = this.clientService.selectedClientBs$.subscribe(client => {
			this.updateBreadcrumbs();
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['selectedClient']) {
			this.updateBreadcrumbs();
		}
	}

	private updateBreadcrumbs() {
		this.sub?.unsubscribe();

		this.sub = combineLatest([
			this.nav.routeParts$,
			this.adminService.selectedClient$,
			this.adminService.selectedCourse$,
			this.adminService.selectedUnit$,
			this.adminService.selectedLesson$,
			this.adminService.selectedActivity$,
			this.adminService.selectedContent$,
			this.adminService.selectedUserPool$,
			this.adminService.selectedUser$,
		])
			.pipe(
				map(async ([routeParts, client, course, unit, lesson, activity, content]) => {
					const tempBreadcrumbs: Breadcrumb[] = [
						{ name: 'Home', route: ['/'], label: 'Home' },
						{ name: 'Admin', route: ['/', 'my', 'admin'], label: 'Admin' },
					];

					//the admin product is always after /my/admin but the third part
					//isn't there if we haven't selected a product yet
					const productRoutePart: AdminProducts | undefined = routeParts[2] as AdminProducts | undefined;

					if (productRoutePart === AdminProducts.LC) {
						tempBreadcrumbs.push({
							name: 'Content Management',
							route: ['/', 'my', 'admin', AdminProducts.LC],
							label: 'LC',
						});
					}

					if (productRoutePart === AdminProducts.CONTENT_GENERATION) {
						tempBreadcrumbs.push({
							name: 'Content Generation',
							route: ['/', 'my', 'admin', AdminProducts.CONTENT_GENERATION],
							label: 'Content Generation',
						});
					}

					if (productRoutePart === AdminProducts.CONTENT_APP) {
						tempBreadcrumbs.push({
							name: 'Content App',
							route: ['/', 'my', 'admin', AdminProducts.CONTENT_APP],
							label: 'Content App',
						});
					}

					if (productRoutePart === AdminProducts.PRODUCT_STATUS) {
						tempBreadcrumbs.push({
							name: 'Product Status',
							route: ['/', 'my', 'admin', AdminProducts.PRODUCT_STATUS],
							label: 'Product Status',
						});
					}

					if (productRoutePart === AdminProducts.SETUP_WIZARD) {
						tempBreadcrumbs.push({
							name: 'Setup Wizard',
							route: ['/', 'my', 'admin', AdminProducts.SETUP_WIZARD],
							label: 'Setup Wizard',
						});
					}

					if (productRoutePart === AdminProducts.RUBRICS) {
						tempBreadcrumbs.push({
							name: 'Rubrics',
							route: ['/', 'my', 'admin', AdminProducts.RUBRICS],
							label: 'Rubrics',
						});
					}

					if (productRoutePart === AdminProducts.PRODUCTS) {
						tempBreadcrumbs.push({
							name: 'Products',
							route: ['/', 'my', 'admin', AdminProducts.PRODUCTS],
							label: 'Products',
						});
					}

					if (productRoutePart === AdminProducts.MEDIA_DEPOT) {
						tempBreadcrumbs.push({
							name: 'Media Depot',
							route: ['/', 'my', 'admin', AdminProducts.MEDIA_DEPOT],
							label: 'Media Depot',
						});
					}

					if (productRoutePart === AdminProducts.PURCHASES) {
						tempBreadcrumbs.push({
							name: 'Purchases',
							route: ['/', 'my', 'admin', AdminProducts.PURCHASES],
							label: 'Purchases',
						});
					}

					if (productRoutePart === AdminProducts.STRIDE_ALGORITHM_REPORT) {
						tempBreadcrumbs.push({
							name: 'Stride Algorithm Report',
							route: ['/', 'my', 'admin', AdminProducts.STRIDE_ALGORITHM_REPORT],
							label: 'Stride Algorithm Report',
						});
					}

					// Add dynamic route parts on a per-product basis
					switch (productRoutePart) {
						case AdminProducts.LC:
							tempBreadcrumbs.push(
								...this.addDynamicLcCrumbs(routeParts, client, course, unit, lesson, activity, content)
							);
							break;
						case AdminProducts.IAM:
							tempBreadcrumbs.push(...(await this.addDynamicIamCrumbs(routeParts)));
							break;
						case AdminProducts.PURCHASES:
							tempBreadcrumbs.push(...this.addDynamicPurchasesCrumbs(routeParts));
							break;
						case AdminProducts.SETUP_WIZARD:
							tempBreadcrumbs.push(...(await this.addDynamicSetupWizardCrumbs(routeParts)));
							break;
					}

					return tempBreadcrumbs;
				}),
				switchMap(async items => Promise.all(await items))
			)
			.subscribe((resolvedBreadcrumbs: Breadcrumb[]) => {
				this.breadcrumbs.set(resolvedBreadcrumbs);
			});
	}

	private async addDynamicIamCrumbs(routeParts: string[]) {
		const tempBreadcrumbs: Breadcrumb[] = [];

		if (routeParts.includes('iam')) {
			tempBreadcrumbs.push({
				name: 'User Management',
				route: ['/', 'my', 'admin', 'iam'],
				label: 'IAM',
			});

			const poolName = this.adminService.selectedUserPool$.value?.name;
			if (poolName) {
				tempBreadcrumbs.push({
					name: poolName,
					route: ['/', 'my', 'admin', 'iam'],
					label: 'User Pool',
				});
			}

			const selectedUser = this.adminService.selectedUser$.value;
			if (selectedUser?.uuid) {
				const userUuid = selectedUser.uuid;
				const userPoolUuid = this.adminService.selectedUserPool$.value?.id;

				if (userPoolUuid) {
					tempBreadcrumbs.push(
						await this.cognitoService
							.getDisplayName({
								cognito_uuid: userUuid,
								userPoolId: userPoolUuid,
							})
							.then((resolvedName: string | null) => ({
								name: resolvedName || 'User',
								route: ['/', 'my', 'admin', 'iam', { userPoolUuid }],
								label: 'User',
								userUuid,
								userPoolUuid,
								displayName: resolvedName || 'User',
							}))
							.catch(() => ({
								name: 'User',
								route: ['/', 'my', 'admin', 'iam', { userPoolUuid }],
								label: 'User',
								userUuid,
								userPoolUuid,
								displayName: 'User',
							}))
					);
				} else {
					tempBreadcrumbs.push({
						name: 'User',
						route: ['/', 'my', 'admin', 'iam', { userPoolUuid: '' }],
						label: 'User',
						userUuid,
						userPoolUuid: undefined,
						displayName: 'User',
					});
				}
			}
		}

		return tempBreadcrumbs;
	}

	private addDynamicLcCrumbs(
		routeParts: string[],
		client: Client | undefined,
		course: Course | undefined,
		unit: Unit | undefined,
		lesson: Lesson | undefined,
		activity: Activity | undefined,
		content: Content | undefined
	) {
		const tempBreadcrumbs: Breadcrumb[] = [];

		if (client) {
			tempBreadcrumbs.push({
				name: client.active_text?.name || 'Client',
				route: ['/', 'my', 'admin', 'lc'],
				label: 'Client',
			});
		}

		if (course) {
			tempBreadcrumbs.push({
				name: course.active_text?.name || 'Course',
				route: ['/', 'my', 'admin', 'lc', { clientUuid: client?.uuid }],
				label: 'Course',
			});
		}

		if (unit) {
			tempBreadcrumbs.push({
				name: unit.active_text?.name || 'Unit',
				route: ['/', 'my', 'admin', 'lc', { clientUuid: client?.uuid, courseUuid: course?.uuid }],
				label: 'Unit',
			});
		}

		if (lesson) {
			tempBreadcrumbs.push({
				name: lesson.active_text?.name || 'Lesson',
				route: [
					'/',
					'my',
					'admin',
					'lc',
					{
						clientUuid: client?.uuid,
						courseUuid: course?.uuid,
						unitUuid: unit?.uuid,
					},
				],
				label: 'Lesson',
			});
		}

		if (activity) {
			tempBreadcrumbs.push({
				name: activity.active_text?.name || 'Activity',
				route: [
					'/',
					'my',
					'admin',
					'lc',
					{
						clientUuid: client?.uuid,
						courseUuid: course?.uuid,
						unitUuid: unit?.uuid,
						lessonUuid: lesson?.uuid,
					},
				],
				label: 'Activity',
			});
		}

		if (content) {
			tempBreadcrumbs.push({
				name: content.active_text?.name || 'Content',
				route: [
					'/',
					'my',
					'admin',
					'lc',
					{
						clientUuid: client?.uuid,
						courseUuid: course?.uuid,
						unitUuid: unit?.uuid,
						lessonUuid: lesson?.uuid,
						activityUuid: activity?.uuid,
					},
				],
				label: 'Content',
			});
		}

		return tempBreadcrumbs;
	}

	private addDynamicPurchasesCrumbs(routeParts: string[]) {
		const tempBreadcrumbs: Breadcrumb[] = [];

		const selectedClient = this.selectedClient();

		if (selectedClient) {
			tempBreadcrumbs.push({
				name: selectedClient.active_text?.name || 'Client',
				route: ['/', 'my', 'admin', 'purchases'],
				label: 'Client',
			});
		}

		return tempBreadcrumbs;
	}

	private async addDynamicSetupWizardCrumbs(routeParts: string[]) {
		const tempBreadcrumbs: Breadcrumb[] = [];

		const clientUuid = this.route.snapshot.paramMap.get('clientUuid');

		if (!clientUuid) {
			return tempBreadcrumbs;
		}

		const client: Client | undefined = await this.clientService.get$(clientUuid);

		if (!client) {
			return tempBreadcrumbs;
		}

		tempBreadcrumbs.push({
			name: client.active_text?.name || 'Client',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.CLIENT_PICKER],
			label: 'Client',
		});

		return tempBreadcrumbs;
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.clientSub?.unsubscribe();
	}

	navigate(route: any[]) {
		this.nav.navigate(route);
	}
}
